'use client';
import cn from 'classnames';
import type { KeyboardEvent } from 'react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { updateUsecasePost } from '@cecm-microservices-sdk/core/services/apigateway/update-usecase';
import { FlexibleFormBuilder } from './FlexibleFormBuilder';
import { FlexibleFormAlert } from './FlexibleFormAlert';
import { buildRflzSystemDownMessage } from './rflzResponseHandler';
import { isLicensePlateFormAsset } from './flexibleFormShared';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { Text } from '@/ui-components/atoms/Text/Text';
import { IconArrow } from '@/assets/icons/IconArrow';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { getMicroservicesClient } from '@/services/microservices/client';
import { getMicroservicesCountryCode } from '@/services/microservices/usedCarEligibility';
import styles from './toggleFlexibleForm.module.scss';

const {
    mainWrapper,
    contentWrapper,
    formBreadcrumbStyles,
    firstBreadcrumb,
    followingBreadcrumbs,
    toggleButtonLabelStyles,
    toggleTabList,
    toggleTab,
    toggleTabSelected,
    togglePanelContainer,
} = styles;

interface ToggleFlexibleFormProps {
    asset: SiteAssetGroup;
}

function isFlexibleFormAsset(asset: SiteAsset): asset is SiteFlexibleForm {
    return Array.isArray((asset as SiteFlexibleForm).fields?.form_fields);
}

export function ToggleFlexibleForm({ asset }: ToggleFlexibleFormProps) {
    const formAssets = asset.fields.assets.filter(isFlexibleFormAsset);
    const licensePlateFormId = formAssets.find(isLicensePlateFormAsset)?.cms_id ?? '';
    const toggleId = useId();
    const formFocusRefs = useRef<Record<string, { current: (() => void) | null }>>({});
    const focusTargetRef = useRef<string | null>(null);
    const [value, setValue] = useState<string>(formAssets?.[0]?.cms_id ?? '');
    const [rflzFormErrorMessage, setRflzFormErrorMessage] = useState<React.JSX.Element | undefined>();

    const selectedValue =
        formAssets.length > 0 && formAssets.some((formAsset) => formAsset.cms_id === value)
            ? value
            : (formAssets[0]?.cms_id ?? '');

    useLayoutEffect(() => {
        if (!focusTargetRef.current || focusTargetRef.current !== selectedValue) {
            return;
        }

        formFocusRefs.current[focusTargetRef.current]?.current?.();
        focusTargetRef.current = null;
    }, [selectedValue]);

    useEffect(() => {
        const countryCode = getMicroservicesCountryCode();
        const client = getMicroservicesClient(countryCode);

        if (!client.baseUrl) {
            console.error(
                `Missing microservices base URL for ${countryCode.toUpperCase()}. Check the runtime configuration for the active environment.`,
            );
            return;
        }

        void updateUsecasePost(client, {
            keepCustomerInfo: false,
            useCase: 'SATELLITE_TRIAL',
        }).catch((error) => {
            console.error('Failed to update use case for ToggleFlexibleForm', error);
            setRflzFormErrorMessage(
                <FlexibleFormAlert tone="warning" message={buildRflzSystemDownMessage()} />,
            );
        });
    }, []);

    if (formAssets?.length === 0) {
        return null;
    }

    function getFormFocusRef(formId: string) {
        formFocusRefs.current[formId] ||= { current: null };
        return formFocusRefs.current[formId];
    }

    function selectForm(formId: string, shouldFocus = false) {
        if (!formAssets.some((formAsset) => formAsset.cms_id === formId)) {
            return;
        }

        setValue(formId);
        focusTargetRef.current = shouldFocus ? formId : null;
    }

    function selectTab(nextIndex: number) {
        const nextAsset = formAssets[nextIndex];

        if (!nextAsset) {
            return;
        }

        selectForm(nextAsset.cms_id, true);
    }

    function handleSearchByLicensePlate() {
        if (!licensePlateFormId) {
            return;
        }

        selectForm(licensePlateFormId, true);
    }

    function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
        const lastIndex = formAssets.length - 1;

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            selectTab(index === lastIndex ? 0 : index + 1);
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            selectTab(index === 0 ? lastIndex : index - 1);
        } else if (event.key === 'Home') {
            event.preventDefault();
            selectTab(0);
        } else if (event.key === 'End') {
            event.preventDefault();
            selectTab(lastIndex);
        }
    }

    return (
        <Grid container className={mainWrapper}>
            <Grid col={{ sm: 12, md: 8, lg: 12 }} start={{ md: 3 }}>
                <div className={contentWrapper}>
                    <div className={formBreadcrumbStyles}>
                        <span className={firstBreadcrumb}>Eligibility</span>
                        <IconArrow />
                        <span className={followingBreadcrumbs}>Account</span>
                    </div>
                    <Text tag="h4" variant="h4">
                        {asset.fields.title}
                    </Text>
                    <div
                        role="tablist"
                        aria-label={`${asset.fields.title} form options`}
                        aria-orientation="horizontal"
                        className={toggleTabList}
                    >
                        {formAssets.map((formAsset, index) => {
                            const isSelected = formAsset.cms_id === selectedValue;
                            const tabId = `${toggleId}-tab-${index}`;
                            const panelId = `${toggleId}-panel-${index}`;

                            return (
                                <button
                                    key={formAsset.cms_id}
                                    type="button"
                                    id={tabId}
                                    role="tab"
                                    aria-selected={isSelected}
                                    aria-controls={panelId}
                                    tabIndex={isSelected ? 0 : -1}
                                    className={cn(toggleTab, { [toggleTabSelected]: isSelected })}
                                    onClick={() => selectForm(formAsset.cms_id)}
                                    onKeyDown={(event) => handleTabKeyDown(event, index)}
                                >
                                    <RenderRTE
                                        contentfulDoc={formAsset.fields.headline}
                                        className={toggleButtonLabelStyles}
                                    />
                                </button>
                            );
                        })}
                    </div>
                    <div className={togglePanelContainer}>
                        {formAssets.map((formAsset, index) => {
                            const isSelected = formAsset.cms_id === selectedValue;
                            const tabId = `${toggleId}-tab-${index}`;
                            const panelId = `${toggleId}-panel-${index}`;
                            const shouldUseRflzSubmit =
                                formAsset.fields.type.toLowerCase() === 'rflz' || isLicensePlateFormAsset(formAsset);

                            return (
                                <div
                                    key={formAsset.cms_id}
                                    id={panelId}
                                    role="tabpanel"
                                    aria-labelledby={tabId}
                                    hidden={!isSelected}
                                    aria-hidden={!isSelected}
                                >
                                    <FlexibleFormBuilder
                                        asset={formAsset}
                                        nestedForm
                                        toggleForm
                                        forceRflzSubmit={shouldUseRflzSubmit}
                                        focusFirstFieldRef={getFormFocusRef(formAsset.cms_id)}
                                        onSearchByLicensePlate={handleSearchByLicensePlate}
                                        externalFormErrorMessage={
                                            shouldUseRflzSubmit ? rflzFormErrorMessage : undefined
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Grid>
        </Grid>
    );
}
